<template>
    <SwContainer
        :key="$route.fullPath"
        :class="{
            [$style['catalog']]: true,
            [$style['catalog_loading']]: $apollo.queries.products.loading,
            [$style['catalog_empty']]: isEmpty,
        }"
    >
        <div v-if="isEmpty">
            <div v-if="isSearch">
                <div :class="$style['catalog__header-search']">
                    <h2 :class="[$style['catalog__header-caption'], 'sw-title']">
                        {{ $t('search_noresults_mobile') }} {{ `&#171;${$store.state.catalog.term || $route.query.q}&#187;` }}
                    </h2>

                    <h3 :class="[$style['catalog__header-caption'], 'sw-title']">
                        {{ $t('search_popular_products') }}
                    </h3>
                </div>

                <div :class="$style['catalog__body']">
                    <div :class="$style['catalog__main']">
                        <SwContainer
                            v-if="popularProducts && popularProducts.totalCount"
                            :class="{
                                [$style['catalog__list']]: true,
                                [$style['catalog__list_wide']]: isSearch,
                            }"
                            pa0
                        >
                            <div :class="$style['catalog__list-body']">
                                <ProductItem
                                    v-for="product in popularProducts.edges"
                                    :key="product.slug"
                                    :name="product.name"
                                    :manufacturer="product.manufacturer"
                                    :offers="product.offers"
                                    :image="product.imageUrl"
                                    :labels="product.labels"
                                    :slug="product.slug"
                                    :type="product.type"
                                    :class="$style['catalog__list-item']"
                                    :bonuses-credit="product.bonusesCreditTotal"
                                    light
                                    small
                                />
                            </div>
                        </SwContainer>
                    </div>
                </div>
            </div>

            <div v-else :class="$style['catalog__body']">
                <SvgSpriteNoProducts :class="$style['catalog__image']" />
                <h2 class="sw-title">
                    {{ $t('empty_catalog') }}
                </h2>
            </div>
        </div>

        <div v-else-if="!$apollo.queries.products.loading && filters && filters.length">
            <div :class="$style['catalog__header']">
                <div
                    :class="{
                        [$style['catalog__header-title']]: true,
                        [$style['search']]: isSearch
                    }"
                >
                    <SearchTitle
                        v-if="isSearch"
                        :query="$store.state.catalog.term || $route.query.q"
                        :count="products ? products.totalCount : 0"
                    />

                    <div
                        v-else
                        :class="$style['catalog__header-title-wrap']"
                    >
                        <h2 class="sw-title">
                            {{ catalogTitle }}
                        </h2>

                        <div :class="$style['catalog__products-count']">
                            {{ productsCount }}
                        </div>
                    </div>
                </div>

                <div
                    v-if="(products && products.totalCount || $store.state.catalog.term) && !isSearch"
                    :class="$style['catalog__header-filters']"
                >
                    <SwButton
                        :class="$style['catalog__header-filters-button']"
                        light
                        small
                        @click="open('catalogTree')"
                    >
                        <template #leading-icon>
                            <SvgIconList />
                        </template>

                        {{ $t('categories') }}
                    </SwButton>

                    <SwButton
                        :class="$style['catalog__header-filters-button']"
                        light
                        small
                        @click="open('catalogFilters')"
                    >
                        <template #leading-icon>
                            <SvgIconFilter />
                        </template>

                        {{ $t('filters') }}
                    </SwButton>
                </div>

                <div :class="$style['catalog__header-actions']">
                    <div
                        v-if="showResetButton"
                        :class="[[$style['catalog__header-reset-all']], 'sw-text']"
                        @click="reset"
                    >
                        {{ $t('resetAll') }}
                    </div>

                    <div
                        v-if="products && products.totalCount"
                        :class="{
                            [$style['catalog__header-sorting']]: true,
                            [$style['search']]: isSearch
                        }"
                    >
                        <SortSelect
                            :sorting="sorting"
                            :popup-class="$style['sort-popup']"
                            @change="changeSorting"
                        />
                    </div>
                </div>
            </div>

            <div :class="$style['catalog__body']">
                <div v-if="!isSearch" :class="$style['catalog__aside']">
                    <div :class="$style['catalog__tree']">
                        <client-only>
                            <CatalogTree
                                @category-name="currentCategoryName = $event"
                            />

                            <div v-if="filters">
                                <div :class="[$style['catalog__separator'], 'sw-separator']" />
                                <FilterForm :filters="filters" />
                            </div>
                        </client-only>
                    </div>
                </div>

                <div :class="$style['catalog__main']">
                    <SwContainer
                        v-if="products && products.totalCount"
                        :class="{
                            [$style['catalog__list']]: true,
                            [$style['catalog__list_wide']]: isSearch,
                        }"
                        pa0
                    >
                        <div :class="$style['catalog__list-body']">
                            <ProductItem
                                v-for="product in products.edges"
                                :key="product.slug"
                                :name="product.name"
                                :manufacturer="product.manufacturer"
                                :offers="product.offers"
                                :image="product.imageUrl"
                                :labels="product.labels"
                                :slug="product.slug"
                                :type="product.type"
                                :class="$style['catalog__list-item']"
                                :bonuses-credit="product.bonusesCreditTotal"
                                light
                                small
                            />
                        </div>
                    </SwContainer>

                    <div
                        v-else-if="!$apollo.queries.products.loading"
                        :class="{
                            [$style['catalog__list']]: true,
                            [$style['catalog__list_empty']]: true,
                            [$style['catalog__list_wide']]: isSearch,
                        }"
                    >
                        <SvgSpriteNoProducts :class="$style['catalog__image']" />
                        <h2 class="sw-title">
                            <i18n :tag="false" path="no_products_found">
                                <br>
                            </i18n>
                        </h2>
                    </div>

                    <SwPagination
                        v-if="products"
                        :current="products.currentPage"
                        :total="products.totalCount"
                        :limit="products.limit"
                        :page-count="Math.ceil(products.totalCount / products.limit)"
                        @change="changePage"
                    />
                </div>
            </div>
        </div>
    </SwContainer>
</template>

<script>
import CatalogTree from '@@/website/components/common/CatalogTree'
import ProductItem from '@@/website/components/common/ProductItem'
import SortSelect from '@@/website/components/blocks/BlockCatalog/SortSelect'
import SvgIconFilter from '@@/framework/icons/filter.svg'
import SvgIconList from '@@/framework/icons/list.svg'
import SvgSpriteNoProducts from './BlockCatalog/sprite-no-products.svg'
import SearchTitle from '@@/website/components/blocks/BlockCatalog/SearchTitle'

import {
  GET_CATALOG_FILTERS,
  GET_CATALOG_PRODUCTS
} from '@@/graphQL/web'
import { mapState } from 'vuex'

const sortsMap = {
  cheap_first: 'min',
  expensive_first: 'max',
  novelty: 'new'
}

export default {
  name: 'BlockCatalog',

  components: {
    CatalogTree,
    FilterForm: () => import('@@/website/components/common/FilterForm'),
    ProductItem,
    SearchTitle,
    SortSelect,
    SvgIconFilter,
    SvgIconList,
    SvgSpriteNoProducts
  },

  props: {
    pivotId: {
      type: [Number, String],
      default: null
    },

    title: {
      type: String,
      default: ''
    },

    isSearch: {
      type: Boolean,
      default: false
    },

    config: {
      type: [Object, Array],
      default: () => ({})
    }
  },

  data: () => ({
    filters: null,
    products: null,
    popularProducts: null,
    isPreviewDefaultSort: true,
    currentCategoryName: '',
  }),

  apollo: {
    filters: {
      query: GET_CATALOG_FILTERS,
      variables () {
        return {
          siteCode: this.$store.state.siteCode,
          slug: this.$route.params.product_group_slug || ''
        }
      }
    },

    popularProducts: {
      query: GET_CATALOG_PRODUCTS,
      variables () {
        return {
          siteCode: this.$store.state.siteCode,
          sort: 'novelty',
          limit: 8
        }
      },
      update: data => data.products
    },

    products: {
      query: GET_CATALOG_PRODUCTS,
      variables () {
        return {
          siteCode: this.$store.state.siteCode,
          product_group_slug: this.$route.params.product_group_slug || '',
          search: this.$store.state.catalog.term,
          filters: this.$store.state.catalog.filters,
          sort: this.$store.state.catalog.sort?.sort || this.$store.state.settings.default_sort,
          order: this.$store.state.catalog.sort?.order,
          page: this.$store.state.catalog.page,
          limit: 24
        }
      }
    }
  },

  computed: {
    ...mapState({
      hasCurrentCategoryId: state => !!state.catalog.currentCategoryId,
      hasFilters: state => !!state.catalog.filters.length,
      hasSort: state => state.catalog.sort?.id !== state.catalog.defaultSort,
    }),

    showResetButton () {
      return this.hasCurrentCategoryId || this.hasFilters || this.hasSort
    },

    sorting () {
      // Для конструктора админки сортировку всегда берем из полученных с бека settings
      // Для админ-превью сортировку первый раз берем из полученных с бека settings, затем из стора
      // isPreviewDefaultSort - флаг, позволяющий это сделать, меняем его при смене сортировки в методе changeSorting
      const defaultSort = sortsMap[this.config.default_sorting] || 'new'
      const sort = this.$store.state.catalog.sort?.id || defaultSort

      return this.$store.state.admin && this.isPreviewDefaultSort
        ? defaultSort
        : sort
    },

    isEmpty () {
      return !this.$store.state.catalog.filters.length &&
        !this.$apollo.queries.products.loading &&
        (!this.products || this.products.totalCount === 0)
    },

    catalogTitle () {
      return this.currentCategoryName && !this.isSearch ? this.currentCategoryName : this.title
    },

    productsCount () {
      return `${this.products?.totalCount || 0} ${this.$t('search_results_counts')}`
    }
  },

  watch: {
    products () {
      this.$store.commit('catalog/setTotalProductsCount', this.products?.totalCount || 0)
    }
  },

  created () {
    if (this.$route.name !== 'search') {
      this.$store.commit('catalog/term', '')
    }

    this.$store.commit('catalog/setDefaultSort', sortsMap[this.config.default_sorting])
  },

  methods: {
    open (name) {
      this.$store.commit('modals/open', name)
    },

    changeSorting ({ id, sort, order }) {
      this.$store.commit('catalog/sort', { id, sort, order })
      if (this.$route.path.includes('preview')) {
        this.isPreviewDefaultSort = false
      }
    },

    changePage (page) {
      if (window) {
        window.scrollTo(0, 0)
      }

      this.$store.commit('catalog/page', page)
      this.$router.push({
        path: this.$route.path,
        params: { ...this.$route.params },
        query: {
          ...this.$route.query,
          ...(page > 1 ? { page: page.toString() } : {})
        }
      })
    },

    async reset () {
      await this.$store.dispatch('catalog/reset')

      await this.$router.push({
        path: '/catalog',
        query: {},
        params: { ...this.$route.params }
      })
    },
  }
}
</script>

<style lang="less" module>
@import (reference) "~@omnica/base/assets/stylesheets/index.less";
@import (reference) "@@/assets/Web.less";

.catalog {
  width: 100%;
  max-width: @bp-tablet;
  padding: 40px 0;
  position: relative;
  margin: 0 auto;

  @media screen and (max-width: @bp-tablet) {
    padding: 8px 16px 12px 16px;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;

    @media screen and (max-width: @bp-mobile-small) {
      margin-bottom: 16px;
    }

    @media screen and (max-width: 487.5px) {
      margin-bottom: 8px;
    }

    &-search {
      margin-bottom: 40px;

      @media screen and (max-width: @bp-tablet) {
        margin-top: 16px;
      }
    }

    &-caption {
      display: block;
      margin-top: 0;
      margin-bottom: 24px;
    }
  }

  & &__separator {
    width: calc(100% - 24px);
    margin-left: 16px;
  }

  &__products-count {
    margin-left: 20px;
    font-size: 16px;
    line-height: 24px;
    color: @grey-800;
    transform: translateY(-2px);

    @media screen and (max-width: @bp-tablet) {
      margin-left: 16px;
    }

    @media screen and (max-width: @bp-mobile-small) {
      margin-left: 8px;
      transform: translateY(-3px);
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__header-title {
    &-wrap {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
    }

    @media screen and (max-width: @bp-tablet) {
      width: 100%;
      margin-top: 30px;

      &.search {
        width: inherit;
        margin-bottom: 0;
      }
    }

    @media screen and (max-width: @bp-mobile-big) {
      &.search {
        width: 100%;
      }
    }

    h2 {
      margin: 0;
    }
  }

  &__header-filters {
    display: none;

    @media screen and (max-width: @bp-tablet) {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      width: fit-content;
      padding-top: 16px;
      padding-bottom: 16px;
      margin-right: 16px;
    }

    @media screen and (max-width: @bp-mobile-small) {
      flex-grow: 99;
    }

    @media screen and (max-width: 487.5px) {
      margin-right: 0;
      width: 100%;
    }
  }

  & &__header-filters-button {
    min-width: auto;
    padding: 7px 13px;
    color: var(--sw-color-main-500);

    @media screen and (max-width: 487.5px) {
      width: 100%;
    }
  }

  &__header-filters-button {
    & + & {
      margin-left: 16px;
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
    padding: 10px 0;

    @media screen and (max-width: @bp-mobile-small) {
      justify-content: space-between;
      flex-grow: 1;
    }

    @media screen and (max-width: 487.5px) {
      width: 100%;
    }
  }

  &__header-reset-all {
    margin-right: 40px;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    flex-shrink: 0;

    @media screen and (max-width: @bp-mobile-small) {
      margin-right: 24px;
    }

    &:hover {
      color: @brand-500;
    }
  }

  &__header-sorting {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;

    &.search {
      @media screen and (max-width: @bp-mobile-big) {
        .sort-popup {
          left: 16px;
        }
      }
    }

    @media screen and (max-width: @bp-tablet) {
      align-items: flex-start;

      &.search {
        align-self: center;
      }
    }

    @media screen and (max-width: @bp-mobile-big) {
      align-items: center;

      &.search {
        align-self: inherit;
        margin-top: 0;
      }
    }

    @media screen and (max-width: @bp-mobile-small) {
      margin-left: auto;
    }
  }

  &__body {
    display: flex;
  }

  &__aside {
    width: 246px;
    margin-right: 24px;
    flex-shrink: 0;

    @media screen and (max-width: @bp-tablet) {
      display: none;
    }
  }

  &__tree {
    display: block;
    width: 270px;
    transform: translateX(-16px);

    @media screen and (max-width: @bp-tablet) {
      width: 100%;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__list {
    min-width: 786px;
    max-width: 786px;

    @media screen and (max-width: @bp-tablet) {
      min-width: auto;
      max-width: 100%;
      margin: auto;
    }

    &_wide {
      max-width: 100%;
    }

    &_empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: auto;
      text-align: center;

      @media screen and (max-width: 1200px) {
        margin-top: 50px;
      }
    }
  }

  &__list-body {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-start;
    margin: 0 -24px;
    @media screen and (max-width: @bp-tablet) {
      margin-right: 0;
      margin-left: -24px;
    }
    @media screen and (max-width: 487.5px) {
      margin-left: -@spacing-xs;
    }
  }

  &__list_wide &__list-body {
    justify-content: flex-start;
  }

  &__list-item {
    margin: 0 0 40px 24px;
    @media screen and (max-width: 487.5px) {
      margin: 0 0 @spacing-s @spacing-xs;
    }
  }

  &__image {
    max-width: 100%;
  }

  &_loading {
    min-height: 80vh;
  }

  &_empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .catalog__body {
      flex-direction: column;
      align-items: center;

      h2 {
        text-align: center;
      }
    }
  }

  &&_empty :global(.sw-container__content) {
    flex: 0;
  }
}
</style>
